import { Box, Container, Typography, Button, Grid, alpha, styled } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BrushIcon from '@mui/icons-material/Brush';
import SpeedIcon from '@mui/icons-material/Speed';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useNavigate } from 'react-router-dom';
import { useTenantConfig } from '../hooks/useTenant';

const SectionBadge = styled(Box)({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 14px',
    borderRadius: 50,
    background: 'rgba(8, 145, 178, 0.08)',
    border: '1px solid rgba(8, 145, 178, 0.15)',
    marginBottom: 20,
});

const S = { main: '#0891b2', light: '#22d3ee', dark: '#0e7490' };



const WidgetFreedomSection = () => {
    const { tenantName, widget_video_url } = useTenantConfig();
    const navigate = useNavigate();


    const FEATURES = [
    {
        img: '/assets/icon/instant-message.webp',
        color: S.main,
        bg: 'rgba(8,145,178,0.08)',
        title: 'Gerçek Zamanlı Yönetim Deneyimi',
        desc: 'Klasik yönetim panellerinden farklı olarak yaptığınız tüm değişiklikleri anlık olarak görür, sitenizi gerçek görünümü üzerinden yönetirsiniz. Bu sayede karmaşık ayarlar ile uğraşmadan, hızlı ve kontrollü bir şekilde ilerlersiniz.',
    },
    {
        img: '/assets/icon/color-palette.webp',
        color: '#8b5cf6',
        bg: 'rgba(139,92,246,0.08)',
        title: 'Şablonlara Bağlı Kalmadan Özgün Tasarım',
        desc: `${tenantName}, sizi sabit şablon yapılarına bağlı bırakmaz. Her sayfayı ihtiyacınıza göre özgürce düzenleyebilir, içerikleri dilediğiniz gibi konumlandırabilir ve derneğinizin kurumsal kimliğini yansıtan özgün bir yapı oluşturabilirsiniz.`,
    },
    {
        img: '/assets/icon/seo.webp',
        color: '#10b981',
        bg: 'rgba(16,185,129,0.08)',
        title: 'Yüksek Performans ve SEO Uyumluluğu',
        desc: 'Modern altyapısı sayesinde web siteniz yüksek performansla çalışır, mobil cihazlarda kusursuz görüntülenir ve arama motorları tarafından kolayca indekslenir. Böylece daha hızlı, daha erişilebilir ve daha görünür bir web sitesine sahip olursunuz.',
    },
    {
        img: '/assets/icon/dashboard.webp',
        color: '#f97316',
        bg: 'rgba(249,115,22,0.08)',
        title: 'Modern ve Güçlü Altyapı',
        desc: `${tenantName}, güncel web standartlarına uygun olarak geliştirilmiş modern bir mimari kullanır. Next.js tabanlı bu yapı; yüksek hız, gelişmiş SEO uyumluluğu ve güçlü kullanıcı deneyimi sağlar. Geleneksel altyapılara kıyasla daha hızlı yüklenen, daha güvenli ve sürdürülebilir bir yapı sunar.`,
    },
];

    return (
        <Box sx={{ pt: { xs: 10, md: 14 }, pb: { xs: 10, md: 14 }, background: '#ffffff' }}>
            <Container maxWidth="lg">

                {/* ── ÜST: Başlık (sol) + Video (sağ) ── */}
                <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center" sx={{ mb: { xs: 6, md: 8 } }}>

                    {/* Sol: Başlık + açıklama + buton */}
                    <Grid item xs={12} md={6}>
                        <SectionBadge>
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: S.main }} />
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: S.main, letterSpacing: '0.4px' }}>
                                Sayfanızı Görerek Anında Düzenleyin
                            </Typography>
                        </SectionBadge>

                        <Typography
                            variant="h3"
                            sx={{ fontWeight: 700, fontSize: { xs: '2.2rem', md: '2.8rem' }, color: '#1e293b', lineHeight: 1.25, letterSpacing: '-0.02em', mb: 2 }}
                        >
                            Web Sitenizi Özgürce{' '}
                            <Box component="span" sx={{ color: S.main }}>Şekillendirin</Box>
                        </Typography>

                        <Typography sx={{ color: '#64748b', fontSize: '1rem', lineHeight: 1.75, mb: 4, maxWidth: 500 }}>
                            {tenantName} ile web sitenizi doğrudan sayfa üzerinde görerek düzenleyin.
                            Menüleri, içerik alanlarını, sayfa yapılarını, renkleri ve yazı tiplerini anında değiştirerek tasarımınızı kolayca oluşturun.
                        </Typography>

                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => navigate('/ozellikler/web')}
                            sx={{
                                px: 4, py: 1.5, fontWeight: 600, fontSize: '0.95rem',
                                background: `linear-gradient(135deg, ${S.main} 0%, ${S.dark} 100%)`,
                                borderRadius: 2, textTransform: 'none',
                                boxShadow: `0 4px 16px ${alpha(S.main, 0.3)}`,
                                '&:hover': {
                                    background: `linear-gradient(135deg, ${S.dark} 0%, #164e63 100%)`,
                                    boxShadow: `0 8px 24px ${alpha(S.main, 0.4)}`,
                                    transform: 'translateY(-2px)',
                                },
                            }}
                        >
                            Web Tasarımını Keşfet
                        </Button>
                    </Grid>

                    {/* Sağ: Video */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.1)', border: '1px solid #e2e8f0' }}>
                            <Box
                                component="video"
                                autoPlay loop muted playsInline
                                src={widget_video_url}
                                sx={{ width: '100%', height: 'auto', display: 'block' }}
                            />
                        </Box>
                    </Grid>
                </Grid>

                {/* ── ALT: 2×2 Kartlar ── */}
                <Grid container spacing={3}>
                    {FEATURES.map(({ img, color, bg, title, desc }) => (
                        <Grid item xs={12} sm={6} md={6} key={title}>
                            <Box
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    border: '1px solid #e2e8f0',
                                    bgcolor: '#fafafa',
                                    height: '100%',
                                    transition: 'all 0.25s ease',
                                    '&:hover': {
                                        borderColor: color,
                                        bgcolor: '#fff',
                                        boxShadow: `0 4px 20px ${alpha(color, 0.12)}`,
                                        transform: 'translateY(-4px)',
                                    },
                                }}
                            >
                                <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                    <img src={img} alt={title} style={{ width: 32, height: 32, objectFit: 'contain' }} />
                                </Box>
                                <Typography fontWeight={700} color="#1e293b" fontSize="0.92rem" mb={1}>
                                    {title}
                                </Typography>
                                <Typography variant="body2" color="#64748b" lineHeight={1.7} fontSize="0.83rem">
                                    {desc}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

            </Container>
        </Box>
    );
};

export default WidgetFreedomSection;